import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Popup from 'devextreme-react/popup';
import DataGrid, { Column, Editing, RequiredRule, Lookup, Button as Butt, Paging, SearchPanel, LoadPanel } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { newToast } from '../../../components';
import { useApiExternalRemisions } from '../../../api/api.externalRemisions';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';

interface ModalConfigLaboratoriesProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
}

export const ModalConfigLaboratories = ({ visible, setVisible }: ModalConfigLaboratoriesProps) => {
  const dataGridRef = useRef<DataGrid>(null);
  const { isMedium, isSmall } = useScreenSize();
  const { getExternalLaboratories, createExternalLaboratory, updateExternalLaboratory, deleteExternalLaboratory } = useApiExternalRemisions();
  const [lstContratos, setLstContratos] = useState<any[]>([]);

  const Combobox = useAppSelector((x) => x.configuration.Data?.ComboBox);
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)?.filter((x) => (x.Dfc_Tabla === 'Ordenes' || x.Dfc_Tabla === 'PreOrdenes') && x.Dfc_Estado === true);

  // Cargar los contratos igual que en Orders.tsx
  useEffect(() => {
    if (info && info.length > 0 && info.filter((x) => x.Dfc_Equivalencia === 'Ord_Cn_Id').length > 0) {
      const contratos = info.filter((x) => x.Dfc_Equivalencia === 'Ord_Cn_Id')[0].Dfc_Tbc_Id;
      const contratosData = Combobox?.find((x) => x.Id === contratos)?.Data || [];
      setLstContratos(contratosData);
      return;
    }
    setLstContratos([]);
  }, [info, Combobox]);

  const customData = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Rel_Id',
      load: () =>
        getExternalLaboratories()
          .then((res) => ({
            data: res,
            totalCount: res?.length || 0,
          }))
          .catch(() => {
            throw new Error('Error de carga de laboratorios externos');
          }),
      insert: async (values) => {
        // Asegurar que el estado sea true por defecto si no se especificó
        const dataToInsert = { ...values, Rel_Estado: values.Rel_Estado ?? true };
        return createExternalLaboratory(dataToInsert)
          .then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
              return dataToInsert;
            }
            throw new Error('Error al insertar el laboratorio');
          })
          .catch((error) => {
            console.error(error);
            throw new Error('Error insertando el laboratorio');
          });
      },
      update: (key, values) => {
        return getExternalLaboratories().then((laboratories) => {
          const rowData = laboratories.find((item: any) => item.Rel_Id === key);
          const updatedData = { ...rowData, ...values };

          return updateExternalLaboratory(updatedData, key).then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
              return values;
            }
            throw new Error('Error al actualizar el laboratorio');
          });
        });
      },
      remove: (key) => {
        return deleteExternalLaboratory(key).then((res) => {
          if (res) {
            dataGridRef.current?.instance.refresh();
          }
        });
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleHidden = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  // Manejar cuando se selecciona un contrato para autocompletar el nombre
  const onEditorPreparing = useCallback(
    (e: any) => {
      if (e.dataField === 'Rel_Equivalencia' && e.parentType === 'dataRow') {
        e.editorOptions.onValueChanged = (args: any) => {
          e.setValue(args.value);
          // Buscar el contrato seleccionado y poner su nombre
          const contratoSeleccionado = lstContratos.find((c) => c.Cn_Id === args.value);
          if (contratoSeleccionado && e.row?.data) {
            // Actualizar el nombre en la fila
            e.component.cellValue(e.row.rowIndex, 'Rel_Nombre', contratoSeleccionado.Cn_Nombre);
          }
        };
      }
    },
    [lstContratos]
  );

  // Render de la celda UID en el grid con icono de copiar
  const uidCellRender = useCallback((cellData: any) => {
    if (!cellData.value) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cellData.value}</span>
        <i
          className='dx-icon-copy'
          style={{ cursor: 'pointer', fontSize: '14px', flexShrink: 0, opacity: 0.7 }}
          title='Copiar UID'
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(cellData.value).then(() => {
              newToast('INFO', 'UID copiado al portapapeles', 2);
            });
          }}
        />
      </div>
    );
  }, []);

  // Render de la celda UID en el popup de edición (solo lectura + botón copiar)
  const uidEditCellRender = useCallback((cellInfo: any) => {
    const value = cellInfo.value || cellInfo.data?.Rel_UID;
    if (!value) {
      // Modo agregar: ocultar todo el form item
      return (
        <div
          ref={(el) => {
            if (el) {
              const formItem = el.closest('.dx-field-item');
              if (formItem) (formItem as HTMLElement).style.display = 'none';
            }
          }}
        />
      );
    }
    // Modo editar: mostrar valor con botón de copiar
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          readOnly
          value={value}
          style={{
            flex: 1,
            padding: '7px 10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#f5f5f5',
            color: '#333',
            fontSize: '13px',
            cursor: 'default',
          }}
        />
        <div
          style={{
            cursor: 'pointer',
            padding: '6px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
          }}
          title='Copiar UID'
          onClick={() => {
            navigator.clipboard.writeText(value).then(() => {
              newToast('INFO', 'UID copiado al portapapeles', 2);
            });
          }}
        >
          <i className='dx-icon-copy' style={{ fontSize: '16px' }} />
        </div>
      </div>
    );
  }, []);

  // Inicializar valores por defecto al agregar nueva fila
  const onInitNewRow = useCallback((e: any) => {
    e.data.Rel_Estado = true;
  }, []);

  return (
    <Popup visible={visible} onHidden={handleHidden} title='Configuración de Laboratorios Externos' showTitle={true} showCloseButton={true} width={900} height='auto' maxHeight='90%' fullScreen={isMedium || isSmall}>
      <DataGrid className='grid custom-datagrid' height={500} hoverStateEnabled={true} dataSource={customData} ref={dataGridRef} showColumnLines={true} rowAlternationEnabled={true} highlightChanges={true} repaintChangesOnly={true} width='100%' onEditorPreparing={onEditorPreparing} onInitNewRow={onInitNewRow}>
        <LoadPanel enabled={true} />
        <SearchPanel visible placeholder='Buscar' />
        <Paging enabled={false} />
        <Editing mode='popup' allowUpdating={true} allowAdding={true} allowDeleting={true} />

        <Column dataField='Rel_Equivalencia' caption='CONTRATO' alignment='center' width={180}>
          <Lookup dataSource={lstContratos} displayExpr='Cn_Nombre' valueExpr='Cn_Id' />
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Nombre' caption='NOMBRE' alignment='left'>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_EndPoint' caption='ENDPOINT' alignment='left'>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Token' caption='TOKEN' alignment='left' visible={false}>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_Ip' caption='IP' alignment='left' width={150}>
          <RequiredRule />
        </Column>
        <Column dataField='Rel_UID' caption='UID' alignment='left' width={280} cellRender={uidCellRender} editCellRender={uidEditCellRender} />
        <Column dataField='Rel_Estado' caption='ESTADO' alignment='center' width={70} dataType='boolean' />

        <Column type='buttons' caption='ACCIÓN' width={80}>
          <Butt name='edit' />
          <Butt name='delete' />
        </Column>
      </DataGrid>
    </Popup>
  );
};
